import { useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import SiteHeader from '@/components/feature/SiteHeader';
import SiteFooter from '@/components/feature/SiteFooter';
import BackToTop from '@/components/feature/BackToTop';
import Breadcrumbs from '@/components/feature/Breadcrumbs';
import HotRankList from '@/components/feature/HotRankList';
import Reveal from '@/components/base/Reveal';
import useDocumentMeta from '@/hooks/useDocumentMeta';
import { gossipTopics, hotSearchList } from '@/mocks/gossip';
import type { GossipTopic } from '@/types';
import CommentSection from './components/CommentSection';

const allTopics = gossipTopics as unknown as GossipTopic[];

function formatHeat(value: number) {
  return value >= 10000 ? `${(value / 10000).toFixed(1)}万` : `${value}`;
}

export default function TopicDetail() {
  const { id } = useParams<{ id: string }>();
  const topic = allTopics.find((item) => item.id === id);

  const schema = useMemo(() => {
    if (!topic) return undefined;
    return [
      {
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: topic.title,
        description: topic.summary,
        articleSection: topic.category,
        datePublished: topic.date,
        inLanguage: 'zh-CN',
        keywords: topic.tags.join(','),
        author: { '@type': 'Organization', name: '爆料吃瓜' },
        publisher: { '@type': 'Organization', name: '爆料吃瓜' },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: '/' },
          { '@type': 'ListItem', position: 2, name: '热门瓜料', item: '/topics' },
          { '@type': 'ListItem', position: 3, name: topic.title, item: `/topic/${topic.id}` },
        ],
      },
    ];
  }, [topic]);

  useDocumentMeta({
    title: topic ? `${topic.title} - 事件梳理与讨论 | 爆料吃瓜` : '瓜料未找到 | 爆料吃瓜',
    description: topic
      ? `${topic.summary.slice(0, 90)}… 含事件时间线、来源标注、证据完整度参考与 ${topic.comments} 条网友观点。`
      : '该条瓜料不存在或已被合并，请返回热门瓜料列表查看其他内容。',
    keywords: topic ? [topic.category, topic.mask, ...topic.tags].join(',') : '爆料吃瓜,热门瓜料',
    path: `/topic/${id ?? ''}`,
    ogType: 'article',
    schema,
  });

  if (!topic) {
    return (
      <div className="flex min-h-screen w-full flex-col bg-background-50">
        <SiteHeader />
        <main className="mx-auto flex w-full flex-1 flex-col items-center justify-center px-4 py-24 text-center">
          <i className="ri-emotion-unhappy-line text-5xl text-foreground-300" aria-hidden="true" />
          <h1 className="mt-5 font-heading text-2xl font-black text-foreground-950">这条瓜料不见了</h1>
          <p className="mt-3 max-w-md text-sm leading-relaxed text-foreground-600">
            可能已被合并到其他条目，或者链接不太对。回到列表看看现在大家都在讨论什么。
          </p>
          <Link
            to="/topics"
            className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
          >
            <i className="ri-arrow-left-line" aria-hidden="true" />
            返回热门瓜料
          </Link>
        </main>
        <SiteFooter />
      </div>
    );
  }

  const related = allTopics.filter((item) => topic.related.includes(item.id));

  return (
    <div className="flex min-h-screen w-full flex-col bg-background-50">
      <SiteHeader />

      <main className="flex-1">
        <div className="mx-auto w-full px-4 pt-6 md:px-6 md:pt-8">
          <Breadcrumbs items={[{ label: '热门瓜料', to: '/topics' }, { label: topic.title }]} />
        </div>

        <div className="mx-auto w-full px-4 py-6 md:px-6 md:py-8">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-[1fr_320px]">
            <article>
              <header>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="flex items-center gap-1.5 rounded-md bg-primary-500 px-3 py-1.5 text-xs font-bold text-background-50">
                    <i className="ri-price-tag-3-fill" aria-hidden="true" />
                    {topic.category}
                  </span>
                  <span className="rounded-md bg-accent-200 px-3 py-1.5 text-xs font-semibold text-accent-950">
                    {topic.credibility}
                  </span>
                  <span className="rounded-md bg-secondary-100 px-3 py-1.5 text-xs font-medium text-secondary-800">
                    {topic.mask}
                  </span>
                </div>

                <h1 className="mt-4 font-heading text-2xl font-black leading-snug text-foreground-950 md:text-[34px]">
                  {topic.title}
                </h1>

                <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-foreground-500">
                  <span className="flex items-center gap-1.5">
                    <i className="ri-user-voice-line" aria-hidden="true" />
                    来源：{topic.source}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <i className="ri-time-line" aria-hidden="true" />
                    发布于 {topic.publishedAt}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <i className="ri-fire-line text-primary-500" aria-hidden="true" />
                    热度 {formatHeat(topic.heat)}
                  </span>
                  <a href="#comments" className="flex items-center gap-1.5 transition-colors hover:text-primary-600">
                    <i className="ri-chat-3-line" aria-hidden="true" />
                    {topic.comments} 条讨论
                  </a>
                </div>
              </header>

              <figure className="mt-6">
                <div className="h-[240px] w-full overflow-hidden rounded-lg md:h-[420px]">
                  <img
                    src={topic.cover}
                    alt={`${topic.title} 事件相关配图`}
                    title={`${topic.category} · ${topic.title}`}
                    className="h-full w-full object-cover"
                  />
                </div>
                <figcaption className="mt-2 text-[11px] text-foreground-400">
                  配图为氛围示意，非事件现场照片。
                </figcaption>
              </figure>

              <div className="mt-6 grid grid-cols-2 gap-3 md:grid-cols-4">
                {topic.highlights.map((item) => (
                  <div key={item.label} className="rounded-md border border-background-200 bg-background-100 px-3 py-3">
                    <span className="block text-[10px] tracking-wide text-foreground-500">{item.label}</span>
                    <span className="mt-1 block text-xs font-semibold text-foreground-900">{item.value}</span>
                  </div>
                ))}
              </div>

              <div className="mt-8 flex flex-col gap-4">
                {topic.content.map((paragraph, index) => (
                  <p key={paragraph.slice(0, 12)} className="text-[15px] leading-[1.9] text-foreground-700">
                    {index === 0 && (
                      <span className="float-left mr-2 mt-1 flex h-12 w-12 items-center justify-center rounded-md bg-primary-500 font-heading text-xl font-black text-background-50">
                        {topic.category.slice(0, 1)}
                      </span>
                    )}
                    {paragraph}
                  </p>
                ))}
              </div>

              <section className="mt-10 rounded-lg border border-background-200 bg-background-100 p-4 md:p-6">
                <h2 className="flex items-center gap-2 font-heading text-lg font-black text-foreground-950 md:text-xl">
                  <i className="ri-history-line text-secondary-600" aria-hidden="true" />
                  事件时间线
                </h2>
                <ol className="mt-5 flex flex-col">
                  {topic.timeline.map((event, index) => (
                    <li key={event.time} className="relative flex gap-4 pb-6 last:pb-0">
                      <div className="flex flex-col items-center">
                        <span
                          className={`flex h-3 w-3 shrink-0 rounded-full ${
                            index === topic.timeline.length - 1
                              ? 'bg-primary-500'
                              : 'bg-accent-500'
                          }`}
                        />
                        {index !== topic.timeline.length - 1 && (
                          <span className="mt-1 w-px flex-1 bg-background-300" />
                        )}
                      </div>
                      <div className="-mt-0.5">
                        <span className="font-heading text-sm font-bold text-secondary-800">{event.time}</span>
                        <p className="mt-1 text-[13px] leading-relaxed text-foreground-600">{event.text}</p>
                      </div>
                    </li>
                  ))}
                </ol>
              </section>

              <div className="mt-6 flex flex-wrap items-center gap-2">
                <span className="text-xs text-foreground-500">相关标签：</span>
                {topic.tags.map((tag) => (
                  <Link
                    key={tag}
                    to={`/topics?q=${encodeURIComponent(tag)}`}
                    className="rounded-full bg-secondary-100 px-3 py-1 text-[11px] font-medium text-secondary-800 transition-colors hover:bg-accent-100 hover:text-accent-900"
                  >
                    #{tag}
                  </Link>
                ))}
              </div>

              <div className="mt-6 flex items-start gap-3 rounded-lg border border-accent-300 bg-accent-50 p-4">
                <i className="ri-alert-line mt-0.5 text-lg text-accent-700" aria-hidden="true" />
                <p className="text-xs leading-relaxed text-accent-950">
                  本条内容为网友讨论与公开信息整理，不代表事实结论。若你认为其中信息有误，欢迎通过底部邮箱联系我们并附上依据，核实后会更新并标注修改时间。
                </p>
              </div>

              {related.length > 0 && (
                <section className="mt-10">
                  <h2 className="flex items-center gap-2 font-heading text-lg font-black text-foreground-950 md:text-xl">
                    <i className="ri-links-line text-primary-500" aria-hidden="true" />
                    相关瓜料
                  </h2>
                  <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
                    {related.map((item) => (
                      <Reveal key={item.id}>
                        <Link
                          to={`/topic/${item.id}`}
                          className="group flex h-full flex-col rounded-lg border border-background-200 bg-background-50 p-4 transition-colors hover:border-primary-300"
                        >
                          <span className="text-[11px] font-medium text-secondary-700">{item.category}</span>
                          <span className="mt-2 line-clamp-2-custom font-heading text-sm font-bold leading-snug text-foreground-950 transition-colors group-hover:text-primary-700">
                            {item.title}
                          </span>
                          <span className="mt-3 flex items-center gap-3 text-[11px] text-foreground-400">
                            <span className="flex items-center gap-1">
                              <i className="ri-fire-line text-primary-500" aria-hidden="true" />
                              {formatHeat(item.heat)}
                            </span>
                            <span>{item.publishedAt}</span>
                          </span>
                        </Link>
                      </Reveal>
                    ))}
                  </div>
                </section>
              )}

              <CommentSection topicId={topic.id} />
            </article>

            <aside className="flex flex-col gap-6">
              <HotRankList items={hotSearchList} />

              <div className="rounded-lg border border-secondary-200 bg-secondary-950 p-5">
                <span className="flex h-10 w-10 items-center justify-center rounded-md bg-secondary-800 text-accent-400">
                  <i className="ri-group-line text-xl" aria-hidden="true" />
                </span>
                <h2 className="mt-4 font-heading text-base font-bold text-background-50">想找人一起聊？</h2>
                <p className="mt-2 text-[13px] leading-relaxed text-background-300/80">
                  讨论区里有按兴趣划分的群聊和贴吧版面，先看描述再决定进哪间。
                </p>
                <Link
                  to="/community"
                  className="mt-4 inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-accent-500 px-4 py-2.5 text-sm font-semibold text-secondary-950 transition-colors hover:bg-accent-400"
                >
                  查看社群入口
                  <i className="ri-arrow-right-line" aria-hidden="true" />
                </Link>
              </div>

              <div className="rounded-lg border border-background-200 bg-background-50 p-4">
                <h2 className="font-heading text-sm font-bold text-foreground-950">判断小贴士</h2>
                <ul className="mt-3 flex flex-col gap-2.5 text-xs leading-relaxed text-foreground-600">
                  <li className="flex gap-2">
                    <i className="ri-check-line mt-0.5 text-secondary-600" aria-hidden="true" />
                    多机位视频 &gt; 成片对比 &gt; 单张截图
                  </li>
                  <li className="flex gap-2">
                    <i className="ri-check-line mt-0.5 text-secondary-600" aria-hidden="true" />
                    能和既有时间线对上的细节更可信
                  </li>
                  <li className="flex gap-2">
                    <i className="ri-check-line mt-0.5 text-secondary-600" aria-hidden="true" />
                    只有口头爆料时，先保留意见
                  </li>
                </ul>
              </div>
            </aside>
          </div>
        </div>
      </main>

      <SiteFooter />
      <BackToTop />
    </div>
  );
}